<template>
    <div class="app-body" id="view" data-testid="aloai-index-view">
        <div
            v-if="shouldShowAloAi"
            class="container-listing"
        >
            <div class="container-header d-flex">
                <h3 class="listing-heading">AloAi Agents</h3>
                <div class="ml-auto">
                    <el-button
                        type="primary"
                        size="medium"
                        round
                        @click="openCreateAgentDialog"
                        data-testid="aloai-create-agent-btn"
                    >
                        <i class="fa fa-plus mr-1"></i>
                        <strong>New Agent</strong>
                    </el-button>
                </div>
            </div>

            <!-- Search & filter controls -->
            <div class="filter-controls d-flex mb-3">
                <!-- Search input -->
                <el-input
                    v-model="search_query"
                    placeholder="Search by name or ID"
                    prefix-icon="el-icon-search"
                    clearable
                    class="mr-2 search-input"
                    @input="handleSearch"
                    data-testid="aloai-agents-search"
                />

                <!-- Type filter -->
                <el-select
                    v-model="agent_type"
                    filterable
                    placeholder="Select Type"
                    @change="updateFilters"
                    data-testid="aloai-agents-type-filter"
                >
                    <el-option
                        label="All"
                        value="all"
                        data-testid="aloai-agents-type-all"
                    />
                    <el-option
                        label="Text"
                        value="text"
                        data-testid="aloai-agents-type-text"
                    />
                    <el-option
                        label="Voice"
                        value="voice"
                        data-testid="aloai-agents-type-voice"
                    />
                </el-select>

                <!-- Direction filter -->
                <el-select
                    class="ml-2"
                    v-model="agent_direction"
                    filterable
                    placeholder="Select Direction"
                    @change="updateFilters"
                    data-testid="aloai-agents-direction-filter"
                >
                    <el-option
                        label="All"
                        value="all"
                        data-testid="aloai-agents-direction-all"
                    />
                    <el-option
                        label="Inbound"
                        value="inbound"
                        data-testid="aloai-agents-direction-inbound"
                    />
                    <el-option
                        label="Outbound"
                        value="outbound"
                        data-testid="aloai-agents-direction-outbound"
                    />
                </el-select>

                <!-- Status filter -->
                <el-select
                    class="ml-2"
                    v-model="enabled_status"
                    filterable
                    placeholder="Select Status"
                    @change="updateFilters"
                    data-testid="aloai-agents-status-filter"
                >
                    <el-option
                        label="All"
                        value="all"
                        data-testid="aloai-agents-status-all"
                    />
                    <el-option
                        label="Enabled"
                        value="true"
                        data-testid="aloai-agents-status-enabled"
                    />
                    <el-option
                        label="Disabled"
                        value="false"
                        data-testid="aloai-agents-status-disabled"
                    />
                </el-select>

                <!-- Created by filter -->
                <user-selector
                    v-model="created_by"
                    placeholder="Select Creator(s)"
                    class="ml-2 creator-selector"
                    :multiple="true"
                    :hide_extensions="true"
                    collapse-tags
                    @change="onCreatorChange"
                />

                <!-- Clear Filters button -->
                <el-button
                    v-if="hasActiveFilters"
                    type="danger"
                    size="medium"
                    round
                    class="ml-3"
                    @click="clearAllFilters"
                >
                    <i class="fa fa-times mr-1"></i>
                    <strong>Clear Filters</strong>
                </el-button>
            </div>


            <!-- AloAi Agent type -->
            <el-dialog
                title="Choose your AloAi Agent type:"
                :visible.sync="createAgentDialogVisible"
                top="10vh"
                width="45%"
                data-testid="aloai-create-agent-dialog"
            >
                <el-radio-group
                    class="w-100"
                    v-model="selected_agent_type"
                    @change="goToSelectDirection"
                    data-testid="aloai-create-agent-type-group"
                >
                    <div class="row row-filter aloai-agents">
                        <div class="col-md-6">
                            <!-- Text option -->
                            <el-card
                                :shadow="isAloAiTextAgentsEnabled() ? 'hover' : 'never'"
                                class="cursor-pointer agent-option-card"
                                data-testid="aloai-text-agent-card"
                            >
                                <el-radio
                                    label="text"
                                    class="w-100"
                                    :disabled="!isAloAiTextAgentsEnabled()"
                                    data-testid="aloai-text-agent-radio"
                                >
                                    <div class="d-flex align-items-center mb-2">
                                        <i class="text-2x mr-2 material-icons text-indigo-500">sms</i>
                                        <span class="text-lg font-weight-bold text-grey-9">Text</span>
                                    </div>
                                    <p class="text-md l-h">This agent can be used to handle marketing campaigns, sales & support over text.</p>
                                </el-radio>
                            </el-card>
                        </div>
                        <div class="col-md-6">
                            <!-- Voice option -->
                            <el-card
                                :shadow="isAloAiVoiceAgentsEnabled() ? 'hover' : 'never'"
                                class="cursor-pointer agent-option-card"
                                data-testid="aloai-voice-agent-card"
                            >
                                <el-radio
                                    label="voice"
                                    class="w-100"
                                    :disabled="!isAloAiVoiceAgentsEnabled()"
                                    data-testid="aloai-voice-agent-radio"
                                >
                                    <div class="d-flex align-items-center mb-2">
                                        <i class="text-2x mr-2 material-icons text-indigo-500">phone_in_talk</i>
                                        <span class="text-lg font-weight-bold text-grey-9">Voice</span>
                                    </div>
                                    <p class="text-md l-h">This agent can be used to handle inbound and missed calls for different use cases.</p>
                                </el-radio>
                            </el-card>
                        </div>
                    </div>
                </el-radio-group>
            </el-dialog>

            <!-- AloAi Agent direction -->
            <el-dialog
                title="Choose your AloAi Agent direction:"
                :visible.sync="selectAgentDirectionVisible"
                top="10vh"
                width="45%"
                data-testid="aloai-create-agent-direction-dialog"
            >
                <el-radio-group
                    class="w-100"
                    v-model="selected_agent_direction"
                    @change="createAgent"
                    data-testid="aloai-create-agent-direction-group"
                >
                    <!-- Inbound option -->
                    <div class="row row-filter aloai-agents">
                        <div class="col-md-6">
                            <!-- Inbound option -->
                            <el-card
                                shadow="hover"
                                class="cursor-pointer agent-option-card"
                                :shadow="shouldLockInbound ? 'never' : 'hover'"
                                data-testid="aloai-inbound-agent-card"
                            >
                                <el-radio
                                    label="inbound"
                                    class="w-100"
                                    :disabled="shouldLockInbound"
                                    data-testid="aloai-inbound-agent-radio"
                                >
                                    <div class="d-flex align-items-center mb-2">
                                        <i class="text-2x mr-2 material-icons text-indigo-500">call_received</i>
                                        <span class="text-lg font-weight-bold text-grey-9">Inbound</span>
                                    </div>
                                    <p class="text-md l-h">This agent can be used for Q&A, Support and other use cases.</p>
                                </el-radio>
                            </el-card>
                        </div>
                        <div class="col-md-6">
                            <!-- Outbound option -->
                            <el-card
                                class="cursor-pointer agent-option-card"
                                :shadow="shouldLockOutbound ? 'never' : 'hover'"
                                data-testid="aloai-outbound-agent-card"
                            >
                                <el-radio
                                    label="outbound"
                                    class="w-100"
                                    :disabled="shouldLockOutbound"
                                    data-testid="aloai-outbound-agent-radio"
                                >
                                    <div class="d-flex align-items-center mb-2">
                                        <i class="text-2x mr-2 material-icons text-indigo-500">call_made</i>
                                        <span class="text-lg font-weight-bold text-grey-9">Outbound</span>
                                    </div>
                                    <p class="text-md l-h">This agent can be used for initial reach-out for your contacts.</p>
                                </el-radio>
                            </el-card>
                        </div>
                    </div>
                </el-radio-group>
            </el-dialog>

            <!-- AloAi Agents table -->
            <alo-ai-table
                :bots="displayedBots"
                v-loading="is_loading"
                @bot-update="onBotUpdate"
                @bot-delete="onBotDelete"
                data-testid="aloai-agents-table"
            />

            <!-- Pagination -->
            <div class="mt-3" v-if="total_bots > 0">
                <el-pagination
                    :current-page.sync="current_page"
                    :page-size="page_size"
                    :page-sizes="[10, 20, 50, 100]"
                    :total="total_bots"
                    @current-change="handlePageChange"
                    @size-change="pageSizeChange"
                    background
                    class="pull-right"
                    layout="sizes, prev, pager, next"
                    data-testid="aloai-agents-pagination"
                >
                </el-pagination>
                <div class="clearfix"></div>
            </div>
        </div>

        <!-- Promotion -->
        <upgrade-now-page
            image-link="/assets/images/AloAi.svg"
            text="The leading conversational AI supercharging your path to explosive sales growth"
            extra-text="Enhance your team's efficiency and lower operational expenses by automating conversations throughout the entire customer journey. Our AI agent replaces most BDRs and SDRs and will escalate automatically to humans."
            title-text="AloAi Agents"
            kb-link="https://aloware.com/ai-bot/"
            custom-link="https://share.hsforms.com/1RLbs7DggS1i2Lhwld-nwlQ2qfyk"
            class="mt-5"
            v-if="!shouldShowAloAi && shouldShowUpgradeNow"
            data-testid="aloai-upgrade-now-page"
        />
    </div>
</template>

<script>
import { MessageBox } from 'element-ui'
import _ from 'lodash'
import { mapState } from 'vuex'
import auth from '../../auth'
import AloAiTable from '../../components/aloai/aloai-table'
import UpgradeNowPage from "../../components/upgrade-now-page"
import { acl_mixin, aloai_mixin, html_mixin } from '../../mixins'

export default {
    name: 'AloAiIndex',

    components: {
        UpgradeNowPage,
        AloAiTable,
    },

    mixins: [acl_mixin, html_mixin, aloai_mixin],

    data() {
        return {
            auth: auth,
            bots: [],
            agent_type: null,
            createAgentDialogVisible: false,
            agent_direction: null,
            selectAgentDirectionVisible: false,
            current_page: 1,
            page_size: 10,
            total_bots: 0,
            search_query: '',
            is_loading: false,
            created_by: [],
            enabled_status: null,
            selected_agent_type: null,
            selected_agent_direction: null,
        }
    },

    computed: {
        ...mapState('cache', ['current_company']),

        shouldLockInbound() {
            return this.selected_agent_type === 'voice' && !this.isAloAiInboundVoiceAgentsEnabled()
        },

        shouldLockOutbound() {
            return this.selected_agent_type === 'voice' && !this.isAloAiOutboundVoiceAgentsEnabled()
        },


        displayTextRate() {
            if (this.current_company && this.current_company.rate) {
                const rate = parseFloat(this.current_company.rate.aloai_text_bot_enrollment)
                return isNaN(rate) ? '0.10' : rate.toFixed(2)
            }
            return '0.10'
        },

        displayedBots() {
            if (!this.search_query) {
                return this.bots
            }

            const query = this.search_query.toLowerCase()
            return this.bots.filter(bot => {
                const name_match = bot.name.toLowerCase().includes(query)
                const id_match = bot.id.toString().includes(query)
                return name_match || id_match
            })
        },

        hasActiveFilters() {
            return (
                (this.agent_type && this.agent_type !== 'all') ||
                (this.agent_direction && this.agent_direction !== 'all') ||
                (this.enabled_status && this.enabled_status !== 'all') ||
                (this.created_by && this.created_by.length > 0) ||
                (this.search_query && this.search_query.length > 0)
            )
        }
    },

    beforeRouteEnter(to, from, next) {
        // This logic help us wrap the Bots section with the Aloware App components.
        if (to.query.api_token) {
            return next({
                name: 'Login',
                query: {
                    api_token: to.query.api_token
                }
            })
        }

        auth.check().then((res) => {
            if (res.data.user.is_reseller) {
                // redirect to account management portal if the company is a reseller
                next({name: 'Account Management Portal'})
            } else {
                next()
            }
        }).catch((err) => {
            next({
                name: 'Login',
                query: {
                    redirect: to.fullPath
                }
            })
        })
    },

    methods: {
        isNumeric(str) {
            return /^\d+$/.test(str)
        },

        onCreatorChange(val) {
            this.created_by = val
            this.updateFilters()
        },

        // Parse created_by parameter from URL or array
        parseCreatedByParam(param) {
            if (!param) return []

            return Array.isArray(param)
                ? param.map(id => parseInt(id))
                : param.split(',').map(id => parseInt(id))
        },

        // Build query parameters for filtering
        buildQueryParams() {
            const params = {
                page: this.current_page,
                size: this.page_size,
            }

            if (this.agent_type && this.agent_type !== 'all') {
                params.type = this.agent_type
            }
            if (this.agent_direction && this.agent_direction !== 'all') {
                params.direction = this.agent_direction
            }
            if (this.created_by && this.created_by.length > 0) {
                params.created_by = this.created_by.join(',')
            }
            if (this.enabled_status && this.enabled_status !== 'all') {
                params.enabled = this.enabled_status
            }

            return params
        },

        // Initialize filters from URL parameters
        initializeFiltersFromQuery(query) {
            this.agent_type = query.type || null
            this.agent_direction = query.direction || null
            this.enabled_status = query.enabled || null
            this.created_by = this.parseCreatedByParam(query.created_by)
            this.current_page = parseInt(query.page) || 1
        },

        handleSearch: _.debounce(function() {
            // Search if:
            // 1. Query is empty (show all)
            // 2. Query is numeric (ID search)
            // 3. Query has 3 or more characters (name search)
            if (!this.search_query || this.isNumeric(this.search_query) || this.search_query.length >= 3) {
                this.current_page = 1
                this.fetchBots()
            }
        }, 200),
        onBotUpdate(bot) {
            this.bots = this.bots.map((c) => {
                if (c.id === bot.id) {
                    return bot
                }

                return c
            })
        },
        onBotDelete(botId) {
            MessageBox.confirm('Are you sure you want to delete this bot?', 'Warning', {
                confirmButtonText: 'Yes',
                cancelButtonText: 'Cancel',
                type: 'warning'
            }).then(async () => {
                this.is_loading = true

                axios.delete(`/api/v1/aloai/bot/${botId}`).then(res => {
                    this.bots = this.bots.filter((c) => {
                        return c.id !== botId
                    })

                    this.$message({
                        message: 'Bot deleted successfully',
                        type: 'success'
                    })

                    this.fetchBots()
                }).catch(err => {
                    const errorMessage = err.response?.data?.message || 'Failed to delete bot'
                    this.$message({
                        message: errorMessage,
                        type: 'error'
                    })
                    console.log(err)
                }).finally(() => {
                    this.is_loading = false
                })
            }).catch(() => {
                // do nothing
            })
        },
        fetchBots() {
            this.is_loading = true
            const params = {
                page: this.current_page,
                size: this.page_size,
                search: this.search_query && (this.isNumeric(this.search_query) || this.search_query.length >= 3) ? this.search_query : null
            }

            // Add filter parameters
            if (this.agent_type && this.agent_type !== 'all') {
                params.type = this.agent_type
            }
            if (this.agent_direction && this.agent_direction !== 'all') {
                params.direction = this.agent_direction
            }
            if (this.created_by && this.created_by.length > 0) {
                params.created_by = this.created_by.join(',')
            }
            if (this.enabled_status && this.enabled_status !== 'all') {
                params.enabled = this.enabled_status
            }

            // Show system bots like Call Rescue
            params.show_system_bots = true

            axios.get('/api/v1/aloai/bot', { params }).then(res => {
                this.bots = res.data.data
                this.total_bots = res.data.pagination.total
                this.is_loading = false
            }).catch(err => {
                console.log(err)
                this.is_loading = false
            })
        },
        openCreateAgentDialog() {
            this.createAgentDialogVisible = true
            this.selected_agent_type = null
            this.selected_agent_direction = null
        },
        goToSelectDirection() {
            this.createAgentDialogVisible = false
            this.selectAgentDirectionVisible = true
        },
        createAgent() {
            this.$router.push({name: 'AloAi: Create an agent', query: {type: this.selected_agent_type, direction: this.selected_agent_direction}})
        },
        updateFilters() {
            // Reset to page 1 when filters change
            this.current_page = 1

            const params = this.buildQueryParams()
            this.$router.push({ query: params })
        },
        handlePageChange(page) {
            this.current_page = page

            // Update the URL with the current page
            const params = this.buildQueryParams()
            this.$router.push({ query: params })
        },
        pageSizeChange(size) {
            this.page_size = size
            this.current_page = 1

            const params = this.buildQueryParams()
            this.$router.push({ query: params })
        },
        clearAllFilters() {
            this.search_query = ''
            this.agent_type = null
            this.agent_direction = null
            this.enabled_status = null
            this.created_by = []
            this.current_page = 1

            // Only navigate if there are actually query parameters to clear
            const currentQuery = this.$route.query
            if (Object.keys(currentQuery).length > 0) {
                this.$router.push({ query: {} })
            } else {
                // If no query parameters exist, just fetch the bots directly
                this.fetchBots()
            }
        },
    },

    mounted() {
        this.setPageTitle('AloAi Agents')
        this.initializeFiltersFromQuery(this.$route.query)
        this.fetchBots()

        if (!this.shouldShowAloAiMenu) {
            this.goBack()
        }
    },

    watch: {
        $route({ query }) {
            if (!this.shouldShowAloAiMenu) {
                this.goBack()
            }

            this.initializeFiltersFromQuery(query)
            this.fetchBots()
        }
    }
}
</script>

<style scoped>
.agent-option-card {
  min-height: 150px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.search-input {
  width: 320px;
}

.creator-selector {
  width: 240px;
}
</style>
